import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import WishSingleLineText from "../../../components/WishFormComponents/WishSingleLineText";
import WishFileControl from "../../../components/WishFormComponents/WishFileControl";
import useEnrollment from "../../../services/useEnrollment";
import WishDateControl from "../../../components/WishFormComponents/WishDateControl";
import WishSelect from "../../../components/WishFormComponents/WishSelect";
import moment from "moment";
import EnrollmentContext from "../../../contexts/EnrollUserContext";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const OTHER_RELATIONSHIP_OPTION = "others";
const DECLARATION_BODY =
  "Please note that by making this declaration, you are stating that the person related to you, who was proposed to be your co-applicant, is either deceased or is no longer associated with or relevant to your personal and legal affairs.";
const SwalAlert = withReactContent(Swal);

export default function CoApplicantDetailsPage({ enrollmentMasterData }) {
  const { enrollmentDetails, setEnrollmentDetails, setIsPageDirty, fieldErrors = {}, setFieldErrors } = useContext(EnrollmentContext);
  const clearFieldError = (...keys) => {
    setFieldErrors?.((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      let changed = false;
      keys.forEach((key) => {
        if (next[key]) {
          delete next[key];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  };
  const { getBankBranchDetails, getCoAppRelationships } = useEnrollment(1);
  const [relationships, setRelationships] = useState([]);
  const [otherRelationships, setOtherRelationships] = useState([]);
  const [selectedPrimaryRelationship, setSelectedPrimaryRelationship] = useState("");
  const getCoAppRelationshipsRef = useRef(getCoAppRelationships);

  const updateCoApplicantDetails = useCallback((updates) => {
    setEnrollmentDetails((prevState) => ({
      ...prevState,
      coApplicantDetails: {
        ...prevState.coApplicantDetails,
        ...updates,
      },
    }));
  }, [setEnrollmentDetails]);

  const clearNonDefaultFields = () => ({
    non_default_coapp_reason: "",
    non_default_reason_file: "",
    non_default_coapp_reason_doc_url: "",
    non_default_coapp_reason_doc_name: "",
  });

  const getFileDisplayName = (value, fallback = "") => {
    if (typeof value === "string") return value;
    return value?.name ?? fallback;
  };

  const normalizeSelectValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return "";
    }
    return String(value);
  };

  const coApplicantDetails = enrollmentDetails?.coApplicantDetails ?? {};
  const primaryRelationshipOptions = useMemo(
    () => [...relationships, { id: OTHER_RELATIONSHIP_OPTION, title_name: "Others" }],
    [relationships]
  );

  useEffect(() => {
    getCoAppRelationshipsRef.current = getCoAppRelationships;
  }, [getCoAppRelationships]);

  useEffect(() => {
    const genderId = enrollmentDetails.basicDetails?.gender_id;
    const maritalStatusId = enrollmentDetails.basicDetails?.marital_status_id;

    if (!genderId || !maritalStatusId) {
      return;
    }

    getCoAppRelationshipsRef.current(genderId, maritalStatusId, (data) => {
      const defaultRelationships = data?.relationships ?? [];
      const nonDefaultRelationships = data?.other_relationships ?? [];
      const savedRelationshipId = coApplicantDetails?.coapplicant_relationship_id;
      const savedIsDefault = Number(coApplicantDetails?.is_default_coapplicant ?? 1);
      const defaultRelationshipId = savedIsDefault === 1 ? savedRelationshipId || defaultRelationships[0]?.id || "" : defaultRelationships[0]?.id || "";

      setRelationships(defaultRelationships);
      setOtherRelationships(nonDefaultRelationships);
      setSelectedPrimaryRelationship(savedIsDefault === 0 ? OTHER_RELATIONSHIP_OPTION : normalizeSelectValue(defaultRelationshipId));

      updateCoApplicantDetails({
        coapplicant_relationship_id: savedIsDefault === 0 ? savedRelationshipId ?? "" : defaultRelationshipId,
        is_default_coapplicant: savedIsDefault,
        nominee_mrelationship_id: coApplicantDetails?.nominee_mrelationship_id || enrollmentMasterData?.relationship?.[0]?.id || "",
      });
    });
  }, [coApplicantDetails?.coapplicant_relationship_id, coApplicantDetails?.is_default_coapplicant, coApplicantDetails?.nominee_mrelationship_id, enrollmentDetails.basicDetails?.gender_id, enrollmentDetails.basicDetails?.marital_status_id, enrollmentMasterData?.relationship, updateCoApplicantDetails]);

  const handlePrimaryRelationshipChange = async (newValue) => {
    if (newValue === OTHER_RELATIONSHIP_OPTION) {
      const result = await SwalAlert.fire({
        icon: "warning",
        title: "Please confirm",
        html: `${DECLARATION_BODY}<br/><br/><strong>Do you confirm this declaration?</strong>`,
        showCancelButton: true,
        confirmButtonText: "Yes, I confirm",
        cancelButtonText: "No, I want to change",
        allowOutsideClick: false,
      });

      if (result.isConfirmed) {
        clearFieldError("primary_relationship");
        setSelectedPrimaryRelationship(OTHER_RELATIONSHIP_OPTION);
        updateCoApplicantDetails({
          is_default_coapplicant: 0,
          coapplicant_relationship_id: coApplicantDetails?.is_default_coapplicant === 0 ? coApplicantDetails?.coapplicant_relationship_id : "",
          ...(coApplicantDetails?.is_default_coapplicant === 0 ? {} : clearNonDefaultFields()),
        });
      } else {
        const fallbackRelationshipId = relationships[0]?.id ?? "";
        clearFieldError("coapplicant_relationship_id", "non_default_coapp_reason", "primary_relationship");
        setSelectedPrimaryRelationship(normalizeSelectValue(fallbackRelationshipId));
        updateCoApplicantDetails({
          is_default_coapplicant: 1,
          coapplicant_relationship_id: fallbackRelationshipId,
          ...clearNonDefaultFields(),
        });
      }
      return;
    }

    clearFieldError("coapplicant_relationship_id", "non_default_coapp_reason", "primary_relationship");
    setSelectedPrimaryRelationship(normalizeSelectValue(newValue));
    updateCoApplicantDetails({
      is_default_coapplicant: 1,
      coapplicant_relationship_id: parseInt(newValue, 10),
      ...clearNonDefaultFields(),
    });
  };

  return (
    <div
      onChange={() => {
        setIsPageDirty && setIsPageDirty(true);
      }}
    >
      <WishSingleLineText
        label="Co-Applicant Name"
        id="caName"
        required
        error={fieldErrors.coapplicant_name}
        initialValue={coApplicantDetails?.coapplicant_name}
        onChange={(newValue) => {
          clearFieldError("coapplicant_name");
          updateCoApplicantDetails({ coapplicant_name: newValue.toUpperCase() });
        }}
      />
      <WishSelect
        label="Co-Applicant Relationship"
        id="signupPrimaryRelationship"
        required
        error={fieldErrors.primary_relationship}
        data={primaryRelationshipOptions}
        initialValue={selectedPrimaryRelationship}
        onSelect={handlePrimaryRelationshipChange}
      />
      {Number(coApplicantDetails?.is_default_coapplicant) === 0 ? (
        <>
          <WishSelect
            label="Select Relationship"
            id="signupOtherRelationship"
            required
            error={fieldErrors.coapplicant_relationship_id}
            data={otherRelationships}
            initialValue={normalizeSelectValue(coApplicantDetails?.coapplicant_relationship_id)}
            placeholder
            onSelect={(newValue) => {
              clearFieldError("coapplicant_relationship_id");
              updateCoApplicantDetails({
                coapplicant_relationship_id: newValue ? parseInt(newValue, 10) : "",
              });
            }}
          />
          <div className="form-group row">
            <label htmlFor="coappNonDefaultReason" className="col-md-3 col-lg-3 col-sm-12 col-form-label">
              Reason for Non-Default Co-Applicant
              <span className="text-danger"> *</span>
            </label>
            <div className="col-lg-9 col-md-9 col-sm-12">
              <textarea
                id="coappNonDefaultReason"
                className={"form-control" + (fieldErrors.non_default_coapp_reason ? " is-invalid" : "")}
                rows="4"
                maxLength={500}
                value={coApplicantDetails?.non_default_coapp_reason ?? ""}
                onChange={(e) => {
                  clearFieldError("non_default_coapp_reason");
                  updateCoApplicantDetails({
                    non_default_coapp_reason: e.target.value,
                  });
                }}
              />
              {fieldErrors.non_default_coapp_reason ? (
                <small className="text-danger d-block mt-1">{fieldErrors.non_default_coapp_reason}</small>
              ) : null}
              <small className="text-muted">{(coApplicantDetails?.non_default_coapp_reason ?? "").length}/500</small>
            </div>
          </div>
          <WishFileControl
            label="Supporting Document for Non-default Reason"
            id="coappNonDefaultReasonDoc"
            initialValue={getFileDisplayName(
              coApplicantDetails?.non_default_reason_file,
              coApplicantDetails?.non_default_coapp_reason_doc_name
            )}
            initialUrl={coApplicantDetails?.non_default_coapp_reason_doc_url}
            onChange={(fileName, fileObject) => {
              updateCoApplicantDetails({
                non_default_reason_file: fileObject ?? "",
                non_default_coapp_reason_doc_name: fileObject?.name ?? "",
                non_default_coapp_reason_doc_url: "",
              });
            }}
            onRemove={() => {
              updateCoApplicantDetails({
                non_default_reason_file: "",
                non_default_coapp_reason_doc_name: "",
                non_default_coapp_reason_doc_url: "",
              });
            }}
          />
        </>
      ) : null}
      <WishFileControl
        label="Relationship Proof Document"
        id="coappRelationshipProof"
        initialValue={getFileDisplayName(coApplicantDetails?.coapp_relationship_proof_file, coApplicantDetails?.coapp_relationship_proof_doc_name)}
        initialUrl={coApplicantDetails?.coapp_relationship_proof_url}
        onChange={(fileName, fileObject) => {
          updateCoApplicantDetails({
            coapp_relationship_proof_file: fileObject ?? "",
            coapp_relationship_proof_doc_name: fileObject?.name ?? "",
            coapp_relationship_proof_url: "",
          });
        }}
        onRemove={() => {
          updateCoApplicantDetails({
            coapp_relationship_proof_file: "",
            coapp_relationship_proof_doc_name: "",
            coapp_relationship_proof_url: "",
          });
        }}
      />
      <WishDateControl
        label="Date of birth"
        id="caDOB"
        error={fieldErrors.coapplicant_dob}
        initialValue={coApplicantDetails?.coapplicant_dob ? moment(coApplicantDetails.coapplicant_dob).format("YYYY-MM-DD") : ""}
        onChange={(newValue) => {
          clearFieldError("coapplicant_dob");
          updateCoApplicantDetails({ coapplicant_dob: newValue });
        }}
      />
      <WishSelect
        data={enrollmentMasterData?.gender}
        label="Gender"
        id="caGender"
        initialValue={coApplicantDetails?.coapplicant_gender_id}
        onSelect={(newValue) => {
          updateCoApplicantDetails({ coapplicant_gender_id: parseInt(newValue, 10) });
        }}
      />
      <WishSingleLineText
        label="Mobile Number"
        id="caMobile"
        error={fieldErrors.coapplicant_phone}
        type="number"
        initialValue={coApplicantDetails?.coapplicant_phone}
        onChange={(newValue) => {
          clearFieldError("coapplicant_phone");
          updateCoApplicantDetails({ coapplicant_phone: newValue });
        }}
      />
      <WishSingleLineText
        label="Email Address"
        id="caEmail"
        type="email"
        initialValue={coApplicantDetails?.coapplicant_email}
        onChange={(newValue) => {
          updateCoApplicantDetails({ coapplicant_email: newValue });
        }}
      />
      <WishSingleLineText
        label="PAN Number"
        id="caPan"
        initialValue={coApplicantDetails?.coapplicant_pan_no}
        onChange={(newValue) => {
          updateCoApplicantDetails({ coapplicant_pan_no: newValue.toUpperCase().replace(/\s/g, "") });
        }}
      />
      <WishFileControl
        label="PAN Document (attachment)"
        id="caPANDoc"
        initialValue={getFileDisplayName(coApplicantDetails?.coapp_pan_file)}
        onChange={(fileName, fileObject) => {
          updateCoApplicantDetails({ coapp_pan_file: fileObject ?? "" });
        }}
        onRemove={() => {
          updateCoApplicantDetails({ coapp_pan_file: "" });
        }}
      />
      <WishSingleLineText
        label="Aadhar Number"
        id="caAadhar"
        initialValue={coApplicantDetails?.coapplicant_aadhar_no}
        onChange={(newValue) => {
          updateCoApplicantDetails({ coapplicant_aadhar_no: newValue.toUpperCase().replace(/\s/g, "") });
        }}
      />
      <WishFileControl
        label="Aadhar Document (attachment)"
        id="caAadhar"
        initialValue={getFileDisplayName(coApplicantDetails?.coapp_aadhar_file)}
        onChange={(fileName, fileObject) => {
          updateCoApplicantDetails({ coapp_aadhar_file: fileObject ?? "" });
        }}
        onRemove={() => {
          updateCoApplicantDetails({ coapp_aadhar_file: "" });
        }}
      />
      <WishSingleLineText
        label="Account Name"
        id="caAccName"
        initialValue={coApplicantDetails?.coapplicant_account_name}
        onChange={(newValue) => {
          updateCoApplicantDetails({ coapplicant_account_name: newValue.toUpperCase() });
        }}
      />
      <WishSingleLineText
        label="Account Number"
        id="caAccNumber"
        initialValue={coApplicantDetails?.coapplicant_account_no}
        onChange={(newValue) => {
          updateCoApplicantDetails({ coapplicant_account_no: newValue });
        }}
      />
      <WishSingleLineText
        label="Confirm Account Number"
        id="caConfirmAccNumber"
        initialValue={coApplicantDetails?.coapplicant_confirm_account_no}
        onChange={(newValue) => {
          updateCoApplicantDetails({ coapplicant_confirm_account_no: newValue });
        }}
      />
      <WishSingleLineText
        label="IFSC Code"
        id="caIFSC"
        initialValue={coApplicantDetails?.coapplicant_ifsc}
        onChange={(newValue) => {
          if (newValue.toUpperCase().replace(/\s/g, "").match("^[A-Z]{4}0[A-Z0-9]{6}$")) {
            getBankBranchDetails(newValue, (data) => {
              updateCoApplicantDetails({
                coapplicant_bank_name: data.bank_name,
                coapplicant_branch_name: data.branch_name,
                coapplicant_bank_district: data.bank_district ?? "",
                coapplicant_bank_state: data.branch_state ?? "",
                coapplicant_ifsc: newValue.toUpperCase().replace(/\s/g, ""),
              });
            });
          } else {
            updateCoApplicantDetails({
              coapplicant_bank_name: "",
              coapplicant_branch_name: "",
              coapplicant_bank_district: "",
              coapplicant_bank_state: "",
              coapplicant_ifsc: newValue.toUpperCase().replace(/\s/g, ""),
            });
          }
        }}
      />
      <WishSingleLineText label="Bank Name" readonly id="caBankName" initialValue={coApplicantDetails?.coapplicant_bank_name} />
      <WishSingleLineText label="Bank Branch" readonly id="caBankBranch" initialValue={coApplicantDetails?.coapplicant_branch_name} />
      <WishFileControl
        label="Bank (proof of address)"
        id="caBankDoc"
        initialValue={getFileDisplayName(coApplicantDetails?.coapp_bank_proof_file)}
        onChange={(fileName, fileObject) => {
          updateCoApplicantDetails({ coapp_bank_proof_file: fileObject ?? "" });
        }}
        onRemove={() => {
          updateCoApplicantDetails({ coapp_bank_proof_file: "" });
        }}
      />
      <WishSingleLineText
        label="Nominee Name"
        id="caNomineeName"
        initialValue={coApplicantDetails?.nominee}
        onChange={(newValue) => {
          updateCoApplicantDetails({ nominee: newValue.toUpperCase() });
        }}
      />
      <WishSelect
        label="Nominee Relationship"
        data={enrollmentMasterData?.relationship}
        initialValue={coApplicantDetails?.nominee_mrelationship_id}
        onSelect={(newValue) => {
          updateCoApplicantDetails({ nominee_mrelationship_id: parseInt(newValue, 10) });
        }}
      />
    </div>
  );
}
